import { BrowserRouter, Routes, Route, Navigate, useSearchParams, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { EventsLayout } from '@/components/EventsLayout';
import EventsDashboard from '@/pages/EventsDashboard';
import EventsMecanicas from '@/pages/EventsMecanicas';
import EventsMecanicaForm from '@/pages/EventsMecanicaForm';
import EventsParticipantes from '@/pages/EventsParticipantes';
import EventsGestaoAcesso from '@/pages/EventsGestaoAcesso';
import EventsUsuarioForm from '@/pages/EventsUsuarioForm';
import EventsConfiguracoes from '@/pages/EventsConfiguracoes';
import EventsCatalogo from '@/pages/EventsCatalogo';
import EventsRelatorios from '@/pages/EventsRelatorios';
import UsuarioFila from '@/pages/UsuarioFila';
import Profile from '@/pages/Profile';
import CatalogTokenRedirectPage from '@/pages/CatalogTokenRedirectPage';
import CatalogPage from '@/pages/CatalogPage';
import { IlhaEscura, useModoEfetivo } from '@/components/ds/IlhaEscura';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, eventId } = useAuth();
  const [searchParams] = useSearchParams();

  // Always show loading while auth is being processed
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  // Check if we're receiving token from Doo Events
  const urlToken = searchParams.get('token');
  const urlEventId = searchParams.get('eventId');
  
  // If we have token and eventId in URL, we're in the authentication process
  // Don't redirect immediately - let the auth hook process it
  if (urlToken && urlEventId) {
    // If still loading or not yet authenticated, show loading
    if (isLoading || !isAuthenticated) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Autenticando com Doo Events...</p>
            <p className="text-sm text-muted-foreground mt-2">Evento: {urlEventId}</p>
          </div>
        </div>
      );
    }
    // If authenticated but eventId doesn't match, update it
    if (isAuthenticated && eventId !== urlEventId) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Atualizando contexto do evento...</p>
          </div>
        </div>
      );
    }
  }

  // Check authentication and event context
  // For now, allow access even without authentication
  if (!isAuthenticated || !eventId) {
    console.warn('[Queue] Running without full authentication');
    // Continue anyway - don't redirect
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Área do participante (fila e vitrine): sempre escura, sobre a imagem do evento */}
      <Route element={<IlhaEscura><Outlet /></IlhaEscura>}>
        <Route path="/usuario" element={<UsuarioFila />} />
        <Route path="/acompanhar/:entryId" element={<UsuarioFila />} />

        {/* Public catalog routes — before protected routes */}
        <Route path="/vitrine/:accessToken" element={<CatalogTokenRedirectPage />} />
        <Route path="/vitrine" element={<CatalogPage />} />
      </Route>

      {/* All other routes are protected */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <EventsLayout>
              <Outlet />
            </EventsLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<EventsDashboard />} />
        <Route path="mecanicas" element={<EventsMecanicas />} />
        <Route path="mecanicas/nova" element={<EventsMecanicaForm />} />
        <Route path="mecanicas/:id" element={<EventsMecanicaForm />} />
        <Route path="mecanicas/participantes/:id" element={<EventsParticipantes />} />
        <Route path="participantes" element={<EventsParticipantes />} />
        <Route path="gestao-acesso" element={<EventsGestaoAcesso />} />
        <Route path="usuarios/novo" element={<EventsUsuarioForm />} />
        <Route path="usuarios/:id" element={<EventsUsuarioForm />} />
        <Route path="configuracoes" element={<EventsConfiguracoes />} />
        <Route path="catalogo" element={<EventsCatalogo />} />
        <Route path="relatorios" element={<EventsRelatorios />} />
        <Route path="perfil" element={<Profile />} />
      </Route>
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const modo = useModoEfetivo();
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors theme={modo} />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;